"use client";

import { Call, Sms } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Separator,
  SocialIcon,
} from "@/components/ui";
import { detectSocialLinkName } from "@/lib/utils/social-link.utils";
import type { components } from "@/types/api/api-types";
import { ShopLocation } from "./ShopLocation";

type VendorResponseDto = components["schemas"]["VendorResponseDto"];

interface ShopAboutDrawerProps {
  shopData: VendorResponseDto;
}

export const ShopAboutDrawer = ({ shopData }: ShopAboutDrawerProps) => {
  const t = useTranslations("shop");
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button
          variant={"light"}
          endIcon={<DirectionalChevron size={16} />}
          className="px-0"
        >
          {t("about.viewMore")}
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="justify-center">
          <DrawerTitle className="text-base font-semibold text-center">
            {t("about.title")}
          </DrawerTitle>
        </DrawerHeader>
        <div className="space-y-2">
          <h3 className="font-semibold text-lg">{shopData.shop_name}</h3>
          <ShopLocation
            latitude={shopData.latitude}
            longitude={shopData.longitude}
          />

          <div className="flex flex-col space-y-2">
            {shopData.phone_number && (
              <div className="flex items-center gap-2">
                <Call size={16} />
                <span className="text-sm mt-0.5">{shopData.phone_number}</span>
              </div>
            )}
            {shopData.owner_email && (
              <div className="flex items-center gap-2">
                <Sms size={16} />
                <span className="text-sm mt-0.5">{shopData.owner_email}</span>
              </div>
            )}
            {shopData.vendor_social_links?.map((link) => {
              if (!link?.url) return null;
              const socialName = detectSocialLinkName(link.url);
              const url = link.url.startsWith("http")
                ? link.url
                : `https://${link.url}`;

              return (
                <a
                  key={link.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  <SocialIcon name={socialName} size={16} />
                  <span className="text-sm mt-0.5">{link.url}</span>
                </a>
              );
            })}
          </div>
        </div>
        <DrawerDescription className="mt-5 text-primary">
          {shopData.description}
        </DrawerDescription>

        <Separator orientation="horizontal" className="my-4" />
        <DrawerFooter className="flex flex-row justify-end">
          <DrawerClose asChild>
            <Button variant="solid" colors="secondary" radius={"xs"}>
              {t("about.close")}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
